/**
 * Enum for Journey Builder action types.
 * @readonly
 * @enum {string}
 */
export const JBAction = {
  LOAD_WORKFLOW: 'LOAD_WORKFLOW',
  EXIT_WORKFLOW: 'EXIT_WORKFLOW',
  SAVE_WORKFLOW: 'SAVE_WORKFLOW',

  // TOOLBAR ACTIONS
  ZOOM_IN: 'ZOOM_IN',
  ZOOM_OUT: 'ZOOM_OUT',
  ZOOM_TO_FIT: 'ZOOM_TO_FIT',
  ZOOM_SET: 'ZOOM_SET',
  AUTO_LAYOUT_WORKFLOW: 'AUTO_LAYOUT_WORKFLOW',

  // UI ACTIONS
  PALETTE_SEARCH_TERM_CHANGE: 'PALETTE_SEARCH_TERM_CHANGE',
  PALETTE_VISIBILITY_CHANGE: 'PALETTE_VISIBILITY_CHANGE',
  PROPERTIES_PANEL_OPEN: 'PROPERTIES_PANEL_OPEN',
  PROPERTIES_PANEL_CLOSE: 'PROPERTIES_PANEL_CLOSE',
  REPLACE_ELEMENT_PANEL_OPEN: 'REPLACE_ELEMENT_PANEL_OPEN',
  REPLACE_ELEMENT_PANEL_CLOSE: 'REPLACE_ELEMENT_PANEL_CLOSE',
  CONTEXT_MENU_OPEN: 'CONTEXT_MENU_OPEN',
  CONTEXT_MENU_CLOSE: 'CONTEXT_MENU_CLOSE',
  INFO_PANEL_OPEN: 'INFO_PANEL_OPEN',
  INFO_PANEL_CLOSE: 'INFO_PANEL_CLOSE',

  // DIAGRAM ACTIONS
  BLANK_CLICK: 'BLANK CLICK',
  NODE_SELECTED: 'NODE_SELECTED',
  LINK_SELECTED: 'LINK_SELECTED',
  CELLS_SELECTED: 'CELLS_SELECTED',

  // Error Node
  NODE_ERROR_CLICK: 'NODE_ERROR_CLICK',
  NODE_ERROR_CLEAR: 'NODE_ERROR_CLEAR',
  SCROLL_TO_NODE: 'SCROLL_TO_NODE',
  CLEAR_SCROLL_TO_NODE: 'CLEAR_SCROLL_TO_NODE',

  // Batch update
  BATCH_WORKFLOW_UPDATE: 'BATCH_WORKFLOW_UPDATE',
  UPDATE_WORKFLOW_ERROR_STACK: 'UPDATE_WORKFLOW_ERROR_STACK',
  NODE_VALIDATE: 'NODE_VALIDATE',
  VALIDATE_WORKFLOW: 'VALIDATE_WORKFLOW',

  NODES_MOVED: 'NODES_MOVED',
  NODE_DROPPED_FROM_PALETTE: 'PALETTE_NODE_DROPPED',
  NODE_LABEL_CHANGED: 'NODE_LABEL_CHANGED',
  NODE_DESCRIPTION_CHANGED: 'NODE_DESCRIPTION_CHANGED',
  NODE_DATA_CHANGED: 'NODE_DATA_CHANGED',
  NODE_FORMDATA_CHANGED: 'NODE_FORMDATA_CHANGED',
  NODE_NEXT_CONNECTION_CREATED: 'NODE_NEXT_CONNECTION_CREATED',
  NODE_COPIED: 'NODE_COPIED', // node copied and added into canvas
  NODE_LINKS_REMOVED: 'NODE_LINKS_REMOVED',
  NODE_REMOVED: 'NODE_REMOVED',
  NODE_REPLACE: 'NODE_REPLACE',
  NODE_VALIDATION_CHANGED: 'NODE_VALIDATION_CHANGED',
  BATCH_NODE_VALIDATION_UPDATE: 'BATCH_NODE_VALIDATION_UPDATE',

  VALIDATE_CONNECTION_LIMIT: 'VALIDATE_CONNECTION_LIMIT',

  LINK_CREATED: 'LINK_CREATED',
  LINK_TARGET_REPLACED: 'LINK_TARGET_REPLACED',
  LINK_LABEL_CHANGED: 'LINK_LABEL_CHANGED',
  LINK_REMOVED: 'LINK_REMOVED',

  // CLIPBOARD ACTIONS
  COPY_SELECTION: 'COPY_SELECTION',
  CUT_SELECTION: 'CUT_SELECTION',
  REMOVE_SELECTION: 'REMOVE_SELECTION',
  PASTE: 'PASTE_FROM_CLIPBOARD',

  // HISTORY ACTIONS
  UNDO: 'UNDO',
  REDO: 'REDO',

  // Mock Mode for journey builder
  MOCK_MODE_TOGGLE: 'MOCK_MODE_TOGGLE',
  CLEAR_WORKFLOW: 'CLEAR_WORKFLOW',

  // Paused journey — restricts edits to config-only
  SET_JOURNEY_PAUSED: 'SET_JOURNEY_PAUSED',
  SET_MODE: 'SET_MODE',

  // Halo Actions
  HALO_SEARCH_FILTER: 'HALO_SEARCH_FILTER'
}

export class Actions {
  /**
   * Creates an action to load a workflow.
   * @param {JourneyRawType} data - The workflow data to load.
   * @returns {{type: JBAction, payload: JourneyRawType}} The action object.
   */
  static loadWorkflow(data) {
    return { type: JBAction.LOAD_WORKFLOW, payload: data }
  }

  /**
   * Creates an action to exit the workflow.
   * @returns {{type: JBAction}} The action object.
   */
  static exitWorkflow() {
    return { type: JBAction.EXIT_WORKFLOW }
  }

  /**
   * Creates an action to clear the workflow (reset to empty).
   * @returns {{type: JBAction}} The action object.
   */
  static clearWorkflow() {
    return { type: JBAction.CLEAR_WORKFLOW }
  }

  /**
   * Creates an action to save the workflow.
   * @returns {{type: JBAction}} The action object.
   */
  static saveWorkflow() {
    return { type: JBAction.SAVE_WORKFLOW }
  }

  /**
   * Creates an action to set the palette nodes.
   * @param {Array} nodes - The palette nodes to set.
   * @returns {{type: string, payload: *}} The action object.
   */
  static setPaletteNodes(nodes) {
    return { type: 'SET_PALETTE_NODES', payload: nodes }
  }

  /**
   * Creates an action to toggle the mock mode.
   * @returns {{type: JBAction}} The action object.
   */
  static toggleMockMode(status) {
    return { type: JBAction.MOCK_MODE_TOGGLE, payload: status }
  }

  /**
   * Creates an action to set the journey paused state.
   * When paused, only node config edits (NODE_DATA_CHANGED / NODE_FORMDATA_CHANGED)
   * are permitted; all structural mutations are blocked by the middleware.
   * @param {boolean} paused - Whether the journey is paused.
   * @returns {{type: JBAction, payload: boolean}} The action object.
   */
  static setJourneyPaused(paused) {
    return { type: JBAction.SET_JOURNEY_PAUSED, payload: !!paused }
  }

  /**
   * Creates an action to set the workflow mode (e.g. 'edit', 'view', 'paused').
   * @param {string} mode - The workflow mode.
   * @returns {{type: JBAction, payload: string}} The action object.
   */
  static setMode(mode) {
    return { type: JBAction.SET_MODE, payload: mode }
  }

  /**
   * Creates an action to set the zoom level.
   * @param {number} zoomLevel - The zoom level to set.
   * @returns {{type: JBAction, payload: number}} The action object.
   */
  static zoomSet(zoomLevel) {
    return { type: JBAction.ZOOM_SET, payload: zoomLevel }
  }

  /**
   * Creates an action to zoom in.
   * @returns {{type: JBAction}} The action object.
   */
  static zoomIn() {
    return { type: JBAction.ZOOM_IN }
  }

  /**
   * Creates an action to zoom out.
   * @returns {{type: JBAction}} The action object.
   */
  static zoomOut() {
    return { type: JBAction.ZOOM_OUT }
  }

  /**
   * Creates an action to zoom to fit.
   * @returns {{type: JBAction}} The action object.
   */
  static zoomToFit() {
    return { type: JBAction.ZOOM_TO_FIT }
  }

  /**
   * Creates an action to auto layout workflow nodes.
   * @returns {{type: JBAction}} The action object.
   */
  static autoLayoutWorkflow() {
    return { type: JBAction.AUTO_LAYOUT_WORKFLOW }
  }

  /**
   * Creates an action for a blank click.
   * @returns {{type: JBAction}} The action object.
   */
  static blankClick() {
    return { type: JBAction.BLANK_CLICK }
  }

  /**
   * Creates an action to change the palette visibility.
   * @returns {{type: JBAction}} The action object.
   */
  static paletteVisibilityChange() {
    return { type: JBAction.PALETTE_VISIBILITY_CHANGE }
  }

  /**
   * Creates an action to change the palette search term.
   * @param {string} searchTerm - The new search term.
   * @returns {{type: JBAction, payload: string}} The action object.
   */
  static paletteSearchTermChanged(searchTerm) {
    return { type: JBAction.PALETTE_SEARCH_TERM_CHANGE, payload: searchTerm }
  }

  /**
   * Creates an action to open the context menu.
   * @param {ContextMenuElementType} elementType - The type of the element.
   * @param {DiagramPosition} uiPosition - The position of the context menu.
   * @param {DiagramPosition} canvasPosition - The canvas position of the context menu.
   * @returns {{type: JBAction, payload: {elementType, triggerElement, uiPosition, canvasPosition}}} The action object.
   */
  static contextMenuOpen(elementType, uiPosition, canvasPosition) {
    return {
      type: JBAction.CONTEXT_MENU_OPEN,
      payload: { elementType, uiPosition, canvasPosition }
    }
  }

  /**
   * Creates an action to close the context menu.
   * @returns {{type: JBAction}} The action object.
   */
  static contextMenuClose() {
    return { type: JBAction.CONTEXT_MENU_CLOSE }
  }

  /**
   * Creates an action to open the properties panel.
   * @returns {{type: JBAction}} The action object.
   */
  static propertiesPanelOpen() {
    return { type: JBAction.PROPERTIES_PANEL_OPEN }
  }
  /**
   * Creates an action to close the properties panel.
   * @returns {{type: JBAction}} The action object.
   */
  static propertiesPanelClose() {
    return { type: JBAction.PROPERTIES_PANEL_CLOSE }
  }

  /**
   * Creates an action to select cells.
   * @param {SelectionState} selection - The selected cells.
   * @returns {{type: JBAction, payload: SelectionState}} The action object.
   */
  static cellsSelected(selection) {
    return { type: JBAction.CELLS_SELECTED, payload: selection }
  }

  /**
   * Creates an action to select a node.
   * @param {string} nodeId - The ID of the node.
   * @returns {{type: JBAction, payload: string}} The action object.
   */
  static nodeSelected(nodeId) {
    return { type: JBAction.NODE_SELECTED, payload: nodeId }
  }

  /**
   * Creates an action to select a link.
   * @param {string} linkId - The ID of the link.
   * @returns {{type: JBAction, payload: string}} The action object.
   */
  static linkSelected(linkId) {
    return { type: JBAction.LINK_SELECTED, payload: linkId }
  }

  /**
   * Creates an action to handle a node error click.
   * @param {string} nodeId - The ID of the node with an error.
   * @param {HTMLElement} target - The target element that was clicked.
   * @returns {{type: JBAction, payload: {nodeId: string, target: HTMLElement}}} The action object.
   */
  static nodeErrorClicked(nodeId, target) {
    return { type: JBAction.NODE_ERROR_CLICK, payload: { nodeId, target } }
  }

  /**
   * Creates an action to clear the node error.
   * @returns {{type: JBAction}} The action object.
   */
  static clearNodeError() {
    return { type: JBAction.NODE_ERROR_CLEAR }
  }

  /**
   * Creates an action to clear diagram changes after they've been processed.
   * @returns {{type: string}} The action object.
   */
  static clearDiagramChanges() {
    return { type: 'CLEAR_DIAGRAM_CHANGES' }
  }

  /**
   * Creates an action to filter halo nodes based on search term.
   * @param {*} searchTerm - The search term to filter nodes.
   * @param {*} source - The source node ID for the halo.
   * @returns {{type: JBAction, payload: {searchTerm: *, source: *}}} The action object.
   */
  static haloSearchFilter(searchTerm, source) {
    return { type: JBAction.HALO_SEARCH_FILTER, payload: { searchTerm, source } }
  }

  /**
   * Creates an action to open the info panel.
   * @param {string} nodeId - The ID of the node.
   * @param {any} target - The target element that was mouse hovered.
   * @return {{type: JBAction, payload: {nodeId: string, label: string, description: string, icon: string}}} The action object.
   */
  static infoPanelOpen(nodeId, target) {
    return {
      type: JBAction.INFO_PANEL_OPEN,
      payload: { nodeId, target }
    }
  }

  /**
   * Creates an action to close the info panel.
   * @return {{type: JBAction}} The action object.
   */
  static infoPanelClose() {
    return { type: JBAction.INFO_PANEL_CLOSE }
  }

  /**
   * Creates an action to move nodes.
   * @param {{id: string, position: DiagramPosition}[]} movedNodes - The moved nodes.
   * @returns {{type: JBAction, payload: {id: string, position: DiagramPosition}[]}} The action object.
   */
  static nodesMoved(movedNodes) {
    return { type: JBAction.NODES_MOVED, payload: movedNodes }
  }

  /**
   * Creates an action to drop a node from the palette.
   * @param {string} nodeType - The type of the node.
   * @param {DiagramPosition} position - The position of the node.
   * @returns {{type: JBAction, payload: {type: string, position: DiagramPosition}}} The action object.
   */
  static nodeDropped(nodeData, position) {
    return {
      type: JBAction.NODE_DROPPED_FROM_PALETTE,
      payload: { type: nodeData?.type, position, nodeData: nodeData }
    }
  }

  /**
   * Creates an action to replace a node with a new node type.
   * @param {string} nodeId - The ID of the node to be replaced.
   * @param {string} newNodeType - The type of the new node.
   * @returns {{type: JBAction, payload: {nodeId: string, newNodeType: string, position: DiagramPosition}}} The action object.
   */
  static replaceNode(nodeId, newNodeType) {
    return {
      type: JBAction.NODE_REPLACE,
      payload: { nodeId, newNodeType }
    }
  }

  /**
   * Creates an action to create a next connection for a node.
   * @param {string} nodeId - The ID of the node.
   * @param {string} newNodeType - The type of the new node.
   * @param {DiagramPosition} position - The position of the new node.
   * @returns {{type: JBAction, payload: {nodeId: string, newNodeType: string, position: DiagramPosition}}} The action object.
   */
  static nodeNextConnectionCreated(nodeId, newNodeType, position) {
    return {
      type: JBAction.NODE_NEXT_CONNECTION_CREATED,
      payload: { nodeId, newNodeType, position }
    }
  }

  /**
   * Creates an action to copy a node.
   * @param {string} nodeId - The ID of the node.
   * @param {DiagramPosition} position - The position of the copied node.
   * @returns {{type: JBAction, payload: {nodeId: string, position: DiagramPosition}}} The action object.
   */
  static nodeCopied(nodeId, position) {
    return { type: JBAction.NODE_COPIED, payload: { nodeId, position } }
  }

  /**
   * Creates an action to change the data of a node.
   * @param {string} nodeId - The ID of the node.
   * @param {WorkflowNodeData} data - The new data for the node.
   * @returns {{type: JBAction, payload: {nodeId: string, data: WorkflowNodeData}}} The action object.
   */
  static nodeDataChanged(nodeId, data) {
    return { type: JBAction.NODE_DATA_CHANGED, payload: { nodeId, data } }
  }

  /**
   * Creates an action to change the form data of a node.
   * @param {string} nodeId - The ID of the node.
   * @param {WorkflowNodeData} data - The new data for the node.
   * @returns {{type: JBAction, payload: {nodeId: string, data: WorkflowNodeData}}} The action object.
   */
  static nodeFormDataChanged(nodeId, data) {
    return { type: JBAction.NODE_FORMDATA_CHANGED, payload: { nodeId, data } }
  }

  /**
   * Creates an action to change the validation data of a node.
   * @param {string} nodeId - The ID of the node.
   * @param {WorkflowNodeData} data - The new data for the node.
   * @returns {{type: JBAction, payload: {nodeId: string, data: WorkflowNodeData}}} The action object.
   */
  static nodeValidationDataChanged(nodeId, data) {
    return { type: JBAction.NODE_VALIDATION_CHANGED, payload: { nodeId, data } }
  }

  /**
   * Creates an action to change the validation data of a node.
   * @param {string} nodeId - The ID of the node.
   * @param {WorkflowNodeData} data - The new data for the node.
   * @returns {{type: JBAction, payload: {nodeId: string, data: WorkflowNodeData}}} The action object.
   */
  static nodeBatchValidationUpdate(data) {
    return { type: JBAction.BATCH_NODE_VALIDATION_UPDATE, payload: { data } }
  }

  /**
   * Creates an action to batch update the workflow.
   * @param {Object} data - The new workflow data.
   * @returns {{type: JBAction, payload: {data: Object}}} The action object.
   */
  static batchWorkflowUpdate(data) {
    return { type: JBAction.BATCH_WORKFLOW_UPDATE, payload: { data } }
  }

  /**
   * Creates an action to update the error stack of the workflow.
   * @param {Array} data - The new error stack data.
   * @returns {{type: JBAction, payload: {data: Array}}} The action object.
   */
  static updateWorkflowErrorStack(data) {
    return { type: JBAction.UPDATE_WORKFLOW_ERROR_STACK, payload: { data } }
  }

  /**
   * Creates an action to validate a node.
   * @param {string} nodeId - The ID of the node.
   * @returns {{type: JBAction, payload: {nodeId: string}}} The action object.
   */
  static validateNode(nodeId) {
    return { type: JBAction.NODE_VALIDATE, payload: { nodeId } }
  }

  /**
   * Creates an action to validate the workflow.
   * @returns {{type: JBAction}} The action object.
   */
  static validateWorkflow() {
    return { type: JBAction.VALIDATE_WORKFLOW }
  }

  /**
   * Creates an action to change the label of a node.
   * @param {string} nodeId - The ID of the node.
   * @param {string} label - The new label for the node.
   * @returns {{type: JBAction, payload: {nodeId: string, label: string}}} The action object.
   */
  static nodeLabelChanged(nodeId, label) {
    return { type: JBAction.NODE_LABEL_CHANGED, payload: { nodeId, label } }
  }

  /**
   * Creates an action to change the description of a node.
   * @param {string} nodeId - The ID of the node.
   * @param {string} description - The new description for the node.
   * @returns {{type: JBAction, payload: {nodeId: string, description: string}}} The action object.
   */
  static nodeDescriptionChanged(nodeId, description) {
    return { type: JBAction.NODE_DESCRIPTION_CHANGED, payload: { nodeId, description } }
  }

  /**
   * Creates an action to remove the links of a node.
   * @param {string} nodeId - The ID of the node.
   * @returns {{type: JBAction, payload: string}} The action object.
   */
  static nodeLinksRemoved(nodeId) {
    return { type: JBAction.NODE_LINKS_REMOVED, payload: nodeId }
  }

  /**
   * Creates an action to remove a node.
   * @param {string} nodeId - The ID of the node.
   * @returns {{type: JBAction, payload: string}} The action object.
   */
  static nodeRemoved(nodeId) {
    return { type: JBAction.NODE_REMOVED, payload: nodeId }
  }

  static scrollToNode(nodeId) {
    return { type: JBAction.SCROLL_TO_NODE, payload: nodeId }
  }

  static clearScrollToNode() {
    return { type: JBAction.CLEAR_SCROLL_TO_NODE }
  }

  /**
   * Creates an action to create a link.
   * @param {string} source - The source node ID.
   * @param {string} target - The target node ID.
   * @returns {{type: JBAction, payload: {source: string, target: string}}} The action object.
   */
  static linkCreated(source, target) {
    return { type: JBAction.LINK_CREATED, payload: { source, target } }
  }

  static validateConnectionLimit(nodeId) {
    return { type: JBAction.VALIDATE_CONNECTION_LIMIT, payload: { nodeId } }
  }

  /**
   * Creates an action to update target of a link.
   * @param {string} linkId - The ID of the link.
   * @param {string} source - The source node ID.
   * @param {string} target - The target node ID.
   * @param {any} linkView - The link view object.
   * @returns {{type: JBAction, payload: {linkId: string, source: string, target: string, linkView: any}}} The action object.
   */
  static linkTargetReplaced(linkId, source, target, linkView = null) {
    return { type: JBAction.LINK_TARGET_REPLACED, payload: { linkId, source, target, linkView } }
  }

  /**
   * Creates an action to change the label of a link.
   * @param {string} linkId - The ID of the link.
   * @param {string} label - The new label for the link.
   * @returns {{type: JBAction, payload: {linkId: string, label: string}}} The action object.
   */
  static linkLabelChanged(linkId, label) {
    return { type: JBAction.LINK_LABEL_CHANGED, payload: { linkId, label } }
  }

  /**
   * Creates an action to remove a link.
   * @param {string} linkId - The ID of the link.
   * @param {boolean} [removeCaseCondition=true] - Whether to remove the associated case/condition.
   * @returns {{type: JBAction, payload: string, meta: {removeCaseCondition: boolean}}} The action object.
   */
  static linkRemoved(linkId, removeCaseCondition = true) {
    return { type: JBAction.LINK_REMOVED, payload: linkId, meta: { removeCaseCondition } }
  }

  /**
   * Creates an action to undo the last action.
   * @returns {{type: JBAction}} The action object.
   */
  static undo() {
    return { type: JBAction.UNDO }
  }

  /**
   * Creates an action to redo the last undone action.
   * @returns {{type: JBAction}} The action object.
   */
  static redo() {
    return { type: JBAction.REDO }
  }

  /**
   * Creates an action to copy the current selection.
   * @returns {{type: JBAction}} The action object.
   */
  static copySelection() {
    return { type: JBAction.COPY_SELECTION }
  }

  /**
   * Creates an action to cut the current selection.
   * @returns {{type: JBAction}} The action object.
   */
  static cutSelection() {
    return { type: JBAction.CUT_SELECTION }
  }

  /**
   * Creates an action to paste the selection at a given position.
   * @param {DiagramPosition} position - The position to paste the selection.
   * @returns {{type: JBAction, payload: DiagramPosition}} The action object.
   */
  static pasteSelection(position) {
    return { type: JBAction.PASTE, payload: position }
  }

  /**
   * Creates an action to remove the current selection.
   * @param {boolean} bulkDeletion - Indicates if it is a bulk deletion.
   * @returns {{type: JBAction}} The action object.
   */
  static removeSelection(bulkDeletion = false) {
    return { type: JBAction.REMOVE_SELECTION, meta: { bulkDeletion } }
  }
}
